import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Flag, Compass, Loader2, Trash2, Edit, CheckCircle2, Circle, Clock,
  XCircle, Target as TargetIcon, ChevronDown, ChevronUp, User, Users, MapPin,
} from "lucide-react";

const STATUS_META = {
  pending:      { label: "Pending",     color: "text-slate-400",   bg: "bg-slate-500/15 border-slate-500/25",   icon: Circle },
  in_progress:  { label: "In Progress", color: "text-amber-400",   bg: "bg-amber-500/15 border-amber-500/30",   icon: Clock },
  achieved:     { label: "Achieved",    color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/30", icon: CheckCircle2 },
  dropped:      { label: "Dropped",     color: "text-rose-400",    bg: "bg-rose-500/15 border-rose-500/30",     icon: XCircle },
};

const CATEGORY_BADGE = {
  "Focus Area":    "border-[#C9A24B]/30 text-[#C9A24B] bg-[#C9A24B]/10",
  "Core Objective":"border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
  "Daily Habit":   "border-blue-500/30 text-blue-400 bg-blue-500/10",
  "Every Visit":   "border-purple-500/30 text-purple-400 bg-purple-500/10",
};

const emptyGoal = {
  title: "", description: "", category: "Personal",
  scope: "individual", location: "",
  target_date: "", status: "pending", progress_pct: 0, remarks: "",
};

export default function Goals() {
  const { user } = useAuth();
  const [systemGoals, setSystemGoals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [goals, setGoals] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("individual");  // individual | department | mission
  const [ownerFilter, setOwnerFilter] = useState("me");
  const [locationFilter, setLocationFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyGoal);
  const [saving, setSaving] = useState(false);

  const canPickOwner = user?.role !== "executive";
  const canCreateDept = user?.role === "founder" || user?.role === "manager";

  const loadSystem = () => {
    api.get("/goals/system").then((r) => {
      setSystemGoals(r.data.goals || []);
      setCategories(r.data.categories || []);
    });
    api.get("/locations").then((r) => setLocations(r.data.locations || []));
  };

  const params = useMemo(() => {
    const p = { scope: tab };
    if (statusFilter !== "all") p.status = statusFilter;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (tab === "individual" && canPickOwner) {
      if (ownerFilter && ownerFilter !== "all" && ownerFilter !== "me") p.owner_user_id = ownerFilter;
      else if (ownerFilter === "me") p.owner_user_id = user?.user_id;
    }
    if (tab === "department" && locationFilter && locationFilter !== "All") {
      p.location = locationFilter;
    }
    return p;
  }, [tab, statusFilter, dateFrom, dateTo, ownerFilter, locationFilter, user, canPickOwner]);

  const loadGoals = () => api.get("/goals", { params }).then((r) => setGoals(r.data));

  useEffect(() => { loadSystem(); }, []);
  useEffect(() => { if (tab !== "mission") loadGoals(); }, [params, tab]);
  useEffect(() => {
    if (canPickOwner) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [canPickOwner]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyGoal, scope: tab === "department" ? "department" : "individual" });
    setOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({
      title: g.title, description: g.description || "", category: g.category,
      scope: g.scope || "individual", location: g.location || "",
      target_date: g.target_date || "", status: g.status,
      progress_pct: g.progress_pct || 0, remarks: g.remarks || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Enter a goal title"); return; }
    if (form.scope === "department" && !canCreateDept) {
      toast.error("Only founder / manager can create department goals"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        target_date: form.target_date || null,
        progress_pct: Number(form.progress_pct),
        location: form.scope === "department" ? (form.location || "All") : "",
      };
      if (editing) await api.put(`/goals/${editing.id}`, payload);
      else await api.post("/goals", payload);
      toast.success(editing ? "Goal updated" : "Goal added");
      setOpen(false);
      loadGoals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const removeGoal = async (g) => {
    if (!confirm(`Delete goal "${g.title}"?`)) return;
    try {
      await api.delete(`/goals/${g.id}`);
      toast.success("Goal deleted");
      loadGoals();
    } catch { toast.error("Failed"); }
  };

  const quickStatus = async (g, status) => {
    try {
      await api.put(`/goals/${g.id}`, {
        title: g.title, description: g.description, category: g.category,
        scope: g.scope, location: g.location || "",
        target_date: g.target_date || null, status,
        progress_pct: status === "achieved" ? 100 : g.progress_pct,
        remarks: g.remarks,
      });
      loadGoals();
    } catch { toast.error("Failed"); }
  };

  const systemByCategory = systemGoals.reduce((acc, g) => {
    (acc[g.category] = acc[g.category] || []).push(g);
    return acc;
  }, {});

  const totals = {
    total: goals.length,
    achieved: goals.filter((g) => g.status === "achieved").length,
    in_progress: goals.filter((g) => g.status === "in_progress").length,
    pending: goals.filter((g) => g.status === "pending").length,
  };
  const achievementPct = totals.total ? Math.round((totals.achieved / totals.total) * 100) : 0;

  const TABS = [
    { id: "individual", label: "Individual Goals", icon: User },
    { id: "department", label: "Department Goals", icon: Users },
    { id: "mission", label: "Mission (Auto-linked)", icon: Compass },
  ];

  const canAdd = tab !== "mission" && (tab === "individual" || canCreateDept);

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Direction</div>
          <h2 className="text-3xl font-light text-white mt-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Flag className="text-[#C9A24B]" /> Goals &amp; Objectives
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Individual, department (location-wise) and the always-on Youngness mission — all in one place.
          </p>
        </div>
        {canAdd && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="btn-add-goal" onClick={openNew}
                className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2">
                <Plus size={16} /> Add {tab === "department" ? "Department" : "Individual"} Goal
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Goal" : "New Goal"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {canCreateDept && !editing && (
                  <div>
                    <label className="text-xs text-slate-400">Scope</label>
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                      <SelectTrigger data-testid="goal-scope" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        <SelectItem value="individual">Individual — for one person</SelectItem>
                        <SelectItem value="department">Department — team-wide (location or all)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-xs text-slate-400">Title</label>
                  <input data-testid="goal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Sign 3 new pharmacy tie-ups this month"
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]" />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Description</label>
                  <textarea data-testid="goal-description" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400">Category</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="goal-category" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.scope === "department" && (
                    <div>
                      <label className="text-xs text-slate-400">Location</label>
                      <Select value={form.location || "All"} onValueChange={(v) => setForm({ ...form, location: v })}>
                        <SelectTrigger data-testid="goal-location" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                          <SelectItem value="All">All Locations</SelectItem>
                          {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-slate-400">Target Date</label>
                    <input data-testid="goal-target-date" type="date" value={form.target_date}
                      onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                      className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Status</label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="goal-status" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Progress %</label>
                    <input data-testid="goal-progress" type="number" min={0} max={100} value={form.progress_pct}
                      onChange={(e) => setForm({ ...form, progress_pct: Math.min(100, Math.max(0, Number(e.target.value))) })}
                      className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Remarks / Notes</label>
                  <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none" />
                </div>
              </div>
              <DialogFooter>
                <button data-testid="goal-save" onClick={submit} disabled={saving}
                  className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 flex items-center gap-2">
                  {saving && <Loader2 size={14} className="animate-spin" />} Save
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <div className="flex bg-[#1E293B] border border-slate-800 rounded-xl p-1 gap-1 max-w-2xl" data-testid="goals-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`goals-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === id ? "bg-[#C9A24B] text-[#0F172A]" : "text-slate-300 hover:bg-slate-800/60"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab !== "mission" && (
        <>
          {/* Snapshot */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#1E293B] border border-[#C9A24B]/25 rounded-xl p-5 gold-glow">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Achievement Rate</div>
              <div className="text-white text-3xl font-light mt-2" style={{ fontFamily: "Outfit" }}>{achievementPct}%</div>
              <div className="text-xs text-slate-400 mt-1">{totals.achieved} of {totals.total} goals</div>
            </div>
            {[
              ["In Progress", totals.in_progress, "text-amber-400"],
              ["Pending",     totals.pending,     "text-slate-300"],
              ["Achieved",    totals.achieved,    "text-emerald-400"],
            ].map(([label, val, cls]) => (
              <div key={label} className="bg-[#1E293B] border border-slate-800 rounded-xl p-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
                <div className={`text-3xl font-light mt-2 ${cls}`} style={{ fontFamily: "Outfit" }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-[#1E293B] border border-slate-800 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="goals-filters">
            {tab === "individual" && canPickOwner && (
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger data-testid="goals-owner-filter" className="w-52 bg-slate-900 border-slate-700 h-9">
                  <SelectValue placeholder="Whose goals?" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                  <SelectItem value="me">My goals</SelectItem>
                  <SelectItem value="all">All team goals</SelectItem>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {tab === "department" && (
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger data-testid="goals-location-filter" className="w-48 bg-slate-900 border-slate-700 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                  <SelectItem value="All">All Locations</SelectItem>
                  {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="goals-status-filter" className="w-40 bg-slate-900 border-slate-700 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
              <input data-testid="goals-date-from" type="date" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-transparent text-white px-3 py-1.5 text-sm outline-none" />
              <span className="text-slate-500 px-2">→</span>
              <input data-testid="goals-date-to" type="date" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-transparent text-white px-3 py-1.5 text-sm outline-none" />
            </div>
            <button
              data-testid="goals-reset-filters"
              onClick={() => { setDateFrom(""); setDateTo(""); setStatusFilter("all"); if (canPickOwner) setOwnerFilter("me"); setLocationFilter("All"); }}
              className="text-xs text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 rounded-md px-3 py-1.5"
            >Reset</button>
            <div className="ml-auto text-xs text-slate-400">
              Showing {goals.length} goal{goals.length === 1 ? "" : "s"}
            </div>
          </div>

          {/* Goals list */}
          <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden" data-testid={`${tab}-goals-list`}>
            {goals.length === 0 ? (
              <div className="p-10 text-center">
                <TargetIcon size={28} className="text-slate-600 mx-auto" />
                <div className="text-slate-300 mt-3 font-medium">No {tab === "department" ? "department" : "individual"} goals yet</div>
                <div className="text-xs text-slate-500 mt-1">
                  {tab === "department"
                    ? (canCreateDept ? 'Click "Add Department Goal" — set a location or make it team-wide.' : "The founder or manager will add team-wide goals here.")
                    : 'Click "Add Individual Goal" to write your first personal goal.'}
                </div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-3 font-semibold">Goal</th>
                    <th className="text-left px-4 py-3 font-semibold">Category</th>
                    {tab === "department" && <th className="text-left px-4 py-3 font-semibold">Location</th>}
                    {tab === "individual" && canPickOwner && ownerFilter !== "me" && <th className="text-left px-4 py-3 font-semibold">Owner</th>}
                    <th className="text-left px-4 py-3 font-semibold">Target Date</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-left px-4 py-3 font-semibold">Progress</th>
                    <th className="text-right px-4 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {goals.map((g) => {
                    const meta = STATUS_META[g.status] || STATUS_META.pending;
                    const Icon = meta.icon;
                    const isOwn = g.owner_user_id === user?.user_id;
                    const overdue = g.target_date && g.target_date < todayISO() && g.status !== "achieved" && g.status !== "dropped";
                    const canManageThis = tab === "department" ? canCreateDept : (isOwn || canPickOwner);
                    return (
                      <tr key={g.id} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`goal-row-${g.id}`}>
                        <td className="px-4 py-3">
                          <div className="text-white font-medium">{g.title}</div>
                          {g.description && <div className="text-xs text-slate-400 mt-0.5">{g.description}</div>}
                          {g.remarks && <div className="text-[11px] text-slate-500 mt-1 italic">Note: {g.remarks}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-slate-800/70 border border-slate-700 rounded-full px-2 py-0.5 text-slate-300">{g.category}</span>
                        </td>
                        {tab === "department" && (
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs bg-[#C9A24B]/10 border border-[#C9A24B]/25 text-[#C9A24B] rounded-full px-2 py-0.5">
                              <MapPin size={10} /> {g.location || "All"}
                            </span>
                          </td>
                        )}
                        {tab === "individual" && canPickOwner && ownerFilter !== "me" && (
                          <td className="px-4 py-3 text-slate-300">{g.owner_name}</td>
                        )}
                        <td className={`px-4 py-3 ${overdue ? "text-rose-400 font-semibold" : "text-slate-300"}`}>
                          {fmtDate(g.target_date)}{overdue && <span className="ml-1 text-[10px] uppercase">Overdue</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full border ${meta.bg} ${meta.color}`}>
                            <Icon size={12} /> {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 min-w-[140px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                              <div className={`h-full ${g.progress_pct >= 100 ? "bg-emerald-500" : "bg-[#C9A24B]"}`} style={{ width: `${g.progress_pct}%` }} />
                            </div>
                            <span className="text-xs text-slate-400 w-9 text-right">{g.progress_pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right space-x-1">
                          {canManageThis && g.status !== "achieved" && (
                            <button data-testid={`goal-mark-done-${g.id}`} onClick={() => quickStatus(g, "achieved")}
                              className="text-emerald-400 hover:text-emerald-300" title="Mark achieved">
                              <CheckCircle2 size={15} />
                            </button>
                          )}
                          {canManageThis && (
                            <>
                              <button data-testid={`goal-edit-${g.id}`} onClick={() => openEdit(g)}
                                className="text-[#C9A24B] hover:text-[#b08d40]" title="Edit">
                                <Edit size={14} />
                              </button>
                              <button data-testid={`goal-delete-${g.id}`} onClick={() => removeGoal(g)}
                                className="text-rose-400 hover:text-rose-300" title="Delete">
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === "mission" && (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden" data-testid="system-goals">
          <div className="p-5 border-b border-slate-800 flex items-center gap-2">
            <Compass size={17} className="text-[#C9A24B]" />
            <div>
              <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>The Youngness Mission — Auto-linked</div>
              <div className="text-xs text-slate-400 mt-0.5">Marketing focus areas, core objectives, daily habits and &quot;every visit&quot; reminders — always visible to everyone.</div>
            </div>
          </div>
          <div className="p-5 space-y-5">
            {Object.entries(systemByCategory).map(([cat, items]) => (
              <div key={cat}>
                <div className={`inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${CATEGORY_BADGE[cat] || "border-slate-700 text-slate-300"}`}>
                  {cat}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                  {items.map((g, i) => (
                    <div key={`${cat}-${i}`} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
                      <div className="text-white text-sm font-medium">{g.title}</div>
                      <div className="text-xs text-slate-400 mt-1 leading-relaxed">{g.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
